import { Money } from '../services/models/cart.models';
import { CheckoutStep, CheckoutSteps } from '../services/models/checkout.models';
import { Ga4Event, Ga4Events } from '../services/models/gtm.models';
import { AddressDetails } from './API/Bring/bringAPISchema';
import {
  DeliveryLocation,
  DeliveryMethod,
  DeliveryWindow,
} from './API/Delivery/deliveryAPISchema';

// TEMP: lets us see the "PlacingOrder" loading screen locally. Remove this and its call sites once the loading screen has been reviewed.
export const ARTIFICIAL_ORDER_PLACEMENT_DELAY_MS = 2000;
export const delayForOrderPlacementTesting = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ARTIFICIAL_ORDER_PLACEMENT_DELAY_MS));

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const qualifiesForFreeShipping = (
  freeShippingActive?: boolean,
  freeShippingLimit?: Money,
  subTotal?: Money
): boolean => {
  if (!freeShippingActive || !freeShippingLimit || !subTotal) return false;
  return freeShippingActive && freeShippingLimit.amount <= subTotal.amount;
};

export const amountNeededToQualifyForFreeShipping = (
  freeShippingActive?: boolean,
  freeShippingLimit?: Money,
  value?: Money
): Money | undefined => {
  if (
    !freeShippingActive ||
    !value ||
    !freeShippingLimit ||
    freeShippingLimit.amount <= value.amount
  )
    return;

  return {
    amount: freeShippingLimit.amount - value.amount,
    currency: value.currency,
  };
};

export const formatDeliveryLocationAddress = (location: DeliveryLocation): string => {
  return `${location.deliveryAddress?.trim()}, ${location.postalCode} ${location.city}`;
};

export const setInformationErrors = (errors, informationErrors): void => {
  clearErrors(informationErrors);

  errors.errors.forEach((issue: { path: string | number; message: string }) => {
    informationErrors[issue.path].push(issue.message);
  });
};

export const deliveryWindowTimeStamp = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const formatDay = (date: Date): string => {
  if (isToday(date)) return 'I dag';

  return date.toLocaleDateString('no-NB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
};

export const logoNameForDeliveryMethod = (deliveryMethod: DeliveryMethod): string => {
  switch (deliveryMethod.name) {
    case 'Porterbuddy':
      return 'porterbuddy';
    case 'MyPack':
    case 'HomeDelivery':
      return 'postnord';
    default:
      return '';
  }
};

export const clearErrors = (errorObject): void => {
  Object.keys(errorObject).forEach((key) => (errorObject[key] = []));
};

export const hasDeliveryLocations = (deliveryMethod: DeliveryMethod): boolean => {
  return !!deliveryMethod?.deliveryLocations?.length;
};

export const hasDeliveryWindows = (deliveryMethod: DeliveryMethod): boolean | undefined => {
  return !!deliveryMethod?.deliveryWindows?.length;
};

export const sentenceCase = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const toGoogleAnalyticsEventName = (checkoutStep: CheckoutStep): Ga4Event => {
  switch (checkoutStep) {
    case CheckoutSteps.CustomerInformation:
      return Ga4Events.BeginCheckout;
    case CheckoutSteps.Delivery:
      return Ga4Events.AddShippingInfoAddress;
    case CheckoutSteps.Payment:
      return Ga4Events.AddShippingInfo;
    case CheckoutSteps.Confirmation:
      return Ga4Events.Purchase;
    default:
      return Ga4Events.NoEvent;
  }
};

/**
 * Returns the full street address as a string, e.g. "Main St 12B"
 */
export const formatAddress = (address: AddressDetails): string => {
  const { streetName, houseNumber, letter } = address;
  return `${streetName} ${houseNumber}${letter ?? ''}`.trim();
};

/**
 * Returns the number of delivery windows to show initially, expanded to the nearest
 * page boundary so the selected window is always visible on mount.
 */
export const getInitialVisibleWindowCount = (
  windows: DeliveryWindow[],
  selectedWindow: DeliveryWindow,
  pageSize: number
): number => {
  const selectedIndex = windows.findIndex(
    (w) => w.start === selectedWindow.start && w.end === selectedWindow.end
  );
  if (selectedIndex < 0) return pageSize;
  return Math.ceil((selectedIndex + 1) / pageSize) * pageSize;
};

export type AddressSuggestionOption = {
  heading: string;
  description: string;
  id: string;
};

export const mapAddressesToSuggestionOptions = (addresses: AddressDetails[]): AddressSuggestionOption[] =>
  addresses.map((address) => ({
    heading: formatAddress(address),
    description: [address.postalCode, address.city].filter(Boolean).join(' '),
    id: address.addressId,
  }));

/**
 * Resolves the order-type-specific copy for a fully out-of-stock line item
 * (`currentInventory <= 0`), shared by the line-item pill and `StockSummary`
 * so the two never disagree.
 *
 * - Online (`isClickAndCollect` false): "... online".
 * - Click and Collect with a resolved store name: "... på {storeName}".
 * - Click and Collect without a resolved store name yet: no location clause.
 */
export const resolveOutOfStockMessage = (isClickAndCollect: boolean, storeName?: string): string => {
  if (!isClickAndCollect) {
    return 'Beklager! Ikke tilgjengelig online';
  }
  return storeName ? `Beklager! Ikke tilgjengelig på ${storeName}` : 'Beklager! Ikke tilgjengelig';
};

export const getDifferences = (obj1: any, obj2: any) => {
  const diffs: Record<string, { storeValue: any; apiValue: any }> = {};
  const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
  keys.forEach((key) => {
    if (JSON.stringify(obj1?.[key]) !== JSON.stringify(obj2?.[key])) {
      diffs[key] = { storeValue: obj1?.[key], apiValue: obj2?.[key] };
    }
  });
  return diffs;
};
